import { Divider, IconButton, List, ListItem, ListItemButton, ListItemText, Typography } from "@mui/material"
import { Box, styled } from "@mui/system"
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { useCallback, useState } from "react"
import { convertToY, useYObject } from "../../../hooks/documentHooks"
import { drawerWidth } from "./constants"
import EditorSpace from "./EditorSpace"
import { addVertex, deleteVertex } from "./shapes"

const CustomDrawer = styled('div')({
  display: 'flex',
  width: drawerWidth,
  height: '100%',
  overflowY: 'scroll',
  flexDirection: 'column',
  position: 'absolute'
})

const DrawerHeader = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 8
})

const drawerContainerStyle = { 
  width: '100%', 
  height: 'calc(100% - 50px)', 
  position: 'relative' 
}
const drawerStyle = {
  width: drawerWidth,
  flexShrink: 0,
  '& .MuiDrawer-paper': {
    width: drawerWidth,
    boxSizing: 'border-box',
  },
}

const MapEditor = ({ mapName, mapObject }) => {
  const map = useYObject(mapObject)
  const clipPaths = useYObject(map?.clipPaths)
  const [selectedClipPath, setSelectedClipPath] = useState()
  const [editedClipPath, setEditedClipPath] = useState()
  const [update, setUpdate] = useState()

  const handleSelectClipPath = useCallback(clipPath => {
    if (clipPath === selectedClipPath) {
      if (editedClipPath) {
        setEditedClipPath(null)
      } else {
        setEditedClipPath(clipPath)
      }
    } else {
      setSelectedClipPath(clipPath)
      setEditedClipPath(null)
    }
  }, [selectedClipPath, editedClipPath])

  const handleEditClipPath = useCallback(clipPath => {
    setSelectedClipPath(clipPath)
    setEditedClipPath(clipPath)
  }, [])

  const updateShape = useCallback(() => {
    setUpdate({})
  }, [])

  const newObjClipPath = useCallback((item) => {
    const newId = prompt('Enter id', item.id )
    if(!newId) return
    const newClipPath = {...item, 'id': newId}
    const yClipPath = convertToY(newClipPath)
    map.clipPaths.insert(map.clipPaths.length, [yClipPath])
    setSelectedClipPath(yClipPath)
    setEditedClipPath(yClipPath)
  },[map.clipPaths])

  const deleteClipPath = useCallback((index) => {
    map.clipPaths.delete(index,1)
  },[map.clipPaths])

  return (
    <Box sx={drawerContainerStyle}>
      <CustomDrawer
        variant="permanent"
        sx={drawerStyle}
        anchor="left"
        open
      >
        <DrawerHeader>
          <Typography>
            Clip Paths
          </Typography>
        </DrawerHeader>
        <Divider />
        <List>
          {clipPaths && clipPaths.map((item, index) => {
            const id = item.get('id')
            const selectedId = selectedClipPath && selectedClipPath.get('id')
            return <ClipPathItem 
              key={id+'-'+index} 
              clipPathObject={item} 
              selected={id === selectedId}
              onClick={handleSelectClipPath}
              updateShape={updateShape}
              newObjClipPath={newObjClipPath}
              deleteClipPath={deleteClipPath}
              index={index}
            />
          })}
        </List>
      </CustomDrawer>
      {map?.img &&
        <EditorSpace 
          source={'https://assets.alohome.io' + map.img}
          clipPaths={clipPaths}
          selectedClipPath={selectedClipPath}
          isEditing={!!editedClipPath}
          onClipPathSelected={handleSelectClipPath}
          onClipPathEdited={handleEditClipPath}
          update={update}
        />
      }
    </Box>
  )
}

const ClipPathItem = ({ clipPathObject, selected, onClick, newObjClipPath, updateShape, deleteClipPath, index }) => {
  const clipPath = useYObject(clipPathObject, true)
  const handleSelection = useCallback(() => onClick(clipPathObject), [clipPathObject, onClick])

  const isCircle = clipPath.clipPath.includes('circle')

  const handleAddVertex = useCallback(() => {
    const newClipPath = addVertex(clipPath.clipPath)
    clipPathObject.set('clipPath', newClipPath)
    updateShape()
  }, [clipPath, clipPathObject, updateShape])

  const handleDeleteVertex = useCallback(() => {
    const newClipPath = deleteVertex(clipPath.clipPath)
    clipPathObject.set('clipPath', newClipPath)
    updateShape()
  }, [clipPath, clipPathObject, updateShape])
  
  const handleDuplicateClipPath = useCallback (() => {
    newObjClipPath(clipPath)
  }, [clipPath, newObjClipPath])

  const handleDeleteClipPath = useCallback(() => {
    deleteClipPath(index)
   }, [index, deleteClipPath])
  
   const handleChangeId = useCallback(() => {
    const newId = prompt('Enter new id', clipPathObject.get('id'))
    if (!newId) return
    clipPathObject.set('id', newId)
   },[clipPathObject])

  return (
    <ListItem disablePadding>
      <ListItemButton selected={selected} autoFocus={selected} onClick={handleSelection} onDoubleClick={handleChangeId}>
        <ListItemText primary={clipPath.id} />
      </ListItemButton>
      <div style={{transform: "scale(.7)", width: "30%"}}>
        { selected && <IconButton size="small" onClick={handleDuplicateClipPath}><ContentCopyIcon/></IconButton>}
        { selected && <IconButton size="small" onClick={handleDeleteClipPath}><DeleteOutlineIcon/></IconButton>}
        { selected && !isCircle && <IconButton size="small" onClick={handleAddVertex} ><AddCircleIcon /></IconButton>}
        { selected && !isCircle &&<IconButton size="small" onClick={handleDeleteVertex}><RemoveCircleIcon /></IconButton>}
      </div>
    </ListItem>
  )
}

export default MapEditor
